import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect, useRef } from 'react';
import { X, User, Mail, Phone, CreditCard } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import type { ManualMemberFormData } from '../../types/memberships';
import Modal from '../ui/Modal';

interface PackageOption {
  id: number;
  package_name: string;
  price: number;
  duration: number;
  duration_type: 'day' | 'week' | 'month' | 'year';
  duration_months: number;
}

interface AddManualMemberModalProps {
  isOpen: boolean;
  venueId: number;
  packages: PackageOption[];
  onClose: () => void;
  onSubmit: (data: ManualMemberFormData) => Promise<void>;
  isSaving: boolean;
}

const INITIAL_FORM: ManualMemberFormData = {
  name: '',
  email: '',
  phone: '',
  package_id: '',
  start_date: new Date().toISOString().split('T')[0],
  payment_method: 'cash',
  price: '',
  notes: '',
};

export function AddManualMemberModal({
  isOpen,
  venueId,
  packages,
  onClose,
  onSubmit,
  isSaving,
}: AddManualMemberModalProps) {
  const [form, setForm] = useState<ManualMemberFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Customer autocomplete state
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const customerInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch existing membership customers
  const { data: customers = [] } = useQuery({
    queryKey: ['membership-customers', venueId],
    queryFn: () => vendorApi.getMembershipCustomers(venueId),
    enabled: !!venueId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
      setSearchQuery('');
      setShowCustomerDropdown(false);
    }
  }, [isOpen]);

  // Auto-fill price when package changes
  useEffect(() => {
    if (form.package_id) {
      const pkg = packages.find((p) => p.id === Number(form.package_id));
      if (pkg) {
        setForm((prev) => ({ ...prev, price: pkg.price.toString() }));
      }
    }
  }, [form.package_id, packages]);

  // Debounced customer search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (customers.length > 0) {
        if (searchQuery.length > 0) {
          const filtered = customers
            .filter(customer =>
              customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (customer.phone && customer.phone.includes(searchQuery)) ||
              (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
            )
            .slice(0, 50);
          setFilteredCustomers(filtered);
        } else {
          setFilteredCustomers(customers.slice(0, 50));
        }
      } else {
        setFilteredCustomers([]);
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerInputRef.current && !customerInputRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle customer selection
  const handleSelectCustomer = (customer: { name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }) => {
    setForm((prev) => ({
      ...prev,
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
    }));
    setSearchQuery('');
    setShowCustomerDropdown(false);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.package_id) newErrors.package_id = 'Please select a package';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await onSubmit(form);
  };

  const selectedPkg = packages.find((p) => p.id === Number(form.package_id));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" preventClose={isSaving} animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Add Member</h2>
            <p className="text-xs text-gray-500">Add a walk-in or offline member</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Form */}
        <div className="px-6 py-5 space-y-5">
          {/* Customer Info Section */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Info</h3>

            {/* Name with Autocomplete */}
            <div className="relative" ref={customerInputRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>

              {/* Customer count hint */}
              {customers.length > 0 && (
                <div className="mb-2 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-700">
                  💡 {customers.length} existing customer{customers.length > 1 ? 's' : ''} found. Click to search or type new name.
                </div>
              )}

              <div className="relative">
                <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({ ...prev, name: value }));
                    setSearchQuery(value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => {
                    setSearchQuery(form.name);
                    setShowCustomerDropdown(true);
                  }}
                  placeholder={customers.length > 0 ? "Search existing or type new name..." : "Enter member's name"}
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm outline-none focus:border-blue-500 ${
                    errors.name ? 'border-red-400' : 'border-gray-200'
                  }`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">
                  🔍
                </div>
              </div>

              {/* Customer Autocomplete Dropdown */}
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                  <div className="sticky top-0 p-2 text-xs font-semibold text-blue-800 bg-blue-50 border-b-2 border-blue-200">
                    {searchQuery
                      ? `Found ${filteredCustomers.length} matching customer(s)`
                      : `${filteredCustomers.length} Recent Customer(s)`}
                  </div>
                  {filteredCustomers.map((customer, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-100 focus:outline-none border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 text-sm">{customer.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              customer.source === 'spotts'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {customer.source === 'spotts' ? 'Spotts' : 'Manual'}
                            </span>
                          </div>
                          {customer.phone && (
                            <div className="text-xs text-gray-600 mt-0.5">📱 {customer.phone}</div>
                          )}
                          {customer.email && (
                            <div className="text-xs text-gray-600 mt-0.5">✉️ {customer.email}</div>
                          )}
                        </div>
                        <div className="text-blue-600 text-xs font-medium ml-2">Select →</div>
                      </div>
                    </button>
                  ))}
                  <div className="sticky bottom-0 p-2 text-xs text-center text-gray-500 bg-gray-50 border-t">
                    Or continue typing to add new customer
                  </div>
                </div>
              )}

              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="member@email.com (optional)"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm outline-none focus:border-blue-500 ${
                    errors.email ? 'border-red-400' : 'border-gray-200'
                  }`}
                />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
              <div className="relative">
                <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="080XXXXXXXX (optional)"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Membership Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Membership Details</h3>

            {/* Package */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Package <span className="text-red-500">*</span>
              </label>
              <select
                value={form.package_id}
                onChange={(e) => setForm((prev) => ({ ...prev, package_id: e.target.value ? Number(e.target.value) : '' }))}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-blue-500 bg-white cursor-pointer ${
                  errors.package_id ? 'border-red-400' : 'border-gray-200'
                }`}
              >
                <option value="">Select a package</option>
                {packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.package_name} — {CURRENCY_SYMBOL}{Number(pkg.price).toLocaleString()} ({pkg.duration} {pkg.duration === 1 ? pkg.duration_type : `${pkg.duration_type}s`})
                  </option>
                ))}
              </select>
              {errors.package_id && <p className="text-xs text-red-500 mt-1">{errors.package_id}</p>}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>

            {/* Duration preview */}
            {selectedPkg && (
              <div className="flex justify-between items-center bg-blue-100 rounded-lg p-3 text-sm">
                <span className="text-blue-700">Membership ends</span>
                <span className="font-semibold text-blue-900">
                  {(() => {
                    const startDate = new Date(form.start_date || Date.now());
                    const endDate = new Date(startDate);
                    switch (selectedPkg.duration_type) {
                      case 'day':
                        endDate.setDate(endDate.getDate() + selectedPkg.duration);
                        break;
                      case 'week':
                        endDate.setDate(endDate.getDate() + selectedPkg.duration * 7);
                        break;
                      case 'year':
                        endDate.setFullYear(endDate.getFullYear() + selectedPkg.duration);
                        break;
                      default: // 'month'
                        endDate.setMonth(endDate.getMonth() + selectedPkg.duration);
                        break;
                    }
                    return endDate.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
                  })()}
                </span>
              </div>
            )}
          </div>

          {/* Payment Section */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-semibold text-green-600 uppercase tracking-wider">Payment</h3>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
              <div className="flex gap-2">
                {(['cash', 'transfer', 'pos'] as const).map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, payment_method: method }))}
                    className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all border ${
                      form.payment_method === method
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-green-400'
                    }`}
                  >
                    {method === 'pos' ? 'POS' : method.charAt(0).toUpperCase() + method.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Amount Paid ({CURRENCY_SYMBOL})</label>
              <div className="relative">
                <CreditCard className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                  placeholder="Auto-filled from package"
                  min="0"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Optional notes about this member..."
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500 resize-none"
            />
          </div>
        </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex gap-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 py-3 rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex-1 py-3 rounded-xl font-semibold text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Adding...' : 'Add Member'}
          </button>
        </div>
    </Modal>
  );
}
